<script lang="ts">
  import BottomSheet from './BottomSheet.svelte';
  import type { AppPreferences, ThemeMode } from '../lib/types';
  import { soundManager } from '../lib/soundManager';
  import { exportAppStateJson, importAppStateJson } from '../lib/state.svelte.ts';

  interface Props {
    open: boolean;
    preferences: AppPreferences;
    onupdate: (patch: Partial<AppPreferences>) => void;
    onclose: () => void;
  }

  let { open, preferences, onupdate, onclose }: Props = $props();

  // ─── エクスポート / インポート (全データが対象なのでアプリ設定に配置) ───
  let fileInput = $state<HTMLInputElement | undefined>(undefined);

  function defaultBackupFileName(): string {
    const now = new Date();
    const yyyymmdd = now.toISOString().slice(0, 10).replace(/-/g, '');
    const hhmmss = now.toTimeString().slice(0, 8).replace(/:/g, '');
    return `rroulette-backup-${yyyymmdd}-${hhmmss}.json`;
  }

  /** showSaveFilePicker (File System Access API) が使えるブラウザかどうか */
  function hasSaveFilePicker(): boolean {
    return typeof (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker === 'function';
  }

  async function handleExport(): Promise<void> {
    const json = exportAppStateJson();
    const defaultName = defaultBackupFileName();

    // モダンブラウザ: File System Access API でユーザに保存先・ファイル名を選ばせる
    if (hasSaveFilePicker()) {
      try {
        const handle = await (
          window as unknown as {
            showSaveFilePicker: (opts: unknown) => Promise<{
              createWritable: () => Promise<{
                write: (data: string) => Promise<void>;
                close: () => Promise<void>;
              }>;
              name: string;
            }>;
          }
        ).showSaveFilePicker({
          suggestedName: defaultName,
          types: [
            {
              description: 'RRoulette バックアップ JSON',
              accept: { 'application/json': ['.json'] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(json);
        await writable.close();
        alert(`エクスポート完了\nファイル名: ${handle.name}`);
        return;
      } catch (e) {
        const err = e as Error;
        if (err.name === 'AbortError') return; // ユーザがキャンセル
        // それ以外のエラーは下のフォールバックへ
      }
    }

    // フォールバック: ファイル名を入力させてダウンロード
    const inputName = prompt('保存するファイル名 (拡張子なし)', defaultName.replace(/\.json$/, ''));
    if (inputName === null) return;
    const fileName = inputName.endsWith('.json') ? inputName : `${inputName}.json`;

    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    alert(
      `エクスポート完了\nファイル名: ${fileName}\n保存先: ブラウザのダウンロード設定で指定された場所`,
    );
  }

  async function handleImport(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!confirm('現在のデータが上書きされます。続行しますか？')) {
      input.value = '';
      return;
    }
    const text = await file.text();
    const ok = await importAppStateJson(text);
    if (!ok) {
      alert('インポートに失敗しました。JSON 形式が正しくない可能性があります。');
    } else {
      alert('インポートが完了しました。');
      onclose();
    }
    input.value = '';
  }

  const THEMES: { key: ThemeMode; label: string }[] = [
    { key: 'auto', label: '自動 (OS)' },
    { key: 'light', label: 'ライト' },
    { key: 'dark', label: 'ダーク' },
  ];
</script>

<BottomSheet {open} title="アプリ設定" {onclose}>
  <!-- テーマ -->
  <section class="group">
    <h3 class="group-title">表示</h3>
    <div class="field">
      <div class="field-label" id="theme-label">テーマ</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="theme-label">
        {#each THEMES as o (o.key)}
          <button
            class:active={preferences.themeMode === o.key}
            onclick={() => onupdate({ themeMode: o.key })}
            role="radio"
            aria-checked={preferences.themeMode === o.key}
          >
            {o.label}
          </button>
        {/each}
      </div>
    </div>
  </section>

  <!-- 音量 -->
  <section class="group">
    <h3 class="group-title">サウンド</h3>
    <div class="field">
      <label class="field-label" for="vol">音量: {preferences.volume}%</label>
      <input
        id="vol"
        type="range"
        min="0"
        max="100"
        step="5"
        value={preferences.volume}
        oninput={(e) => {
          const v = +(e.currentTarget as HTMLInputElement).value;
          onupdate({ volume: v });
          soundManager.setVolume(v);
        }}
      />
    </div>
  </section>

  <!-- 項目リスト表示 -->
  <section class="group">
    <h3 class="group-title">項目リスト表示</h3>
    <label class="toggle-row">
      <span>確率 (%) を表示</span>
      <input
        type="checkbox"
        checked={preferences.showItemProbability}
        oninput={(e) =>
          onupdate({ showItemProbability: (e.currentTarget as HTMLInputElement).checked })}
      />
    </label>
    <label class="toggle-row">
      <span>当選回数を表示</span>
      <input
        type="checkbox"
        checked={preferences.showItemWinCount}
        oninput={(e) =>
          onupdate({ showItemWinCount: (e.currentTarget as HTMLInputElement).checked })}
      />
    </label>
  </section>

  <!-- 配置 -->
  <section class="group">
    <h3 class="group-title">配置</h3>
    <label class="toggle-row">
      <span>ランダム時に分割を均等維持</span>
      <input
        type="checkbox"
        checked={preferences.randomMaintainEvenness}
        oninput={(e) =>
          onupdate({
            randomMaintainEvenness: (e.currentTarget as HTMLInputElement).checked,
          })}
      />
    </label>
  </section>

  <!-- データ管理 -->
  <section class="group">
    <h3 class="group-title">データ</h3>
    <div class="data-actions">
      <button class="data-btn" onclick={handleExport}>
        <span>📥 エクスポート</span>
        <span class="sub">全データを JSON で保存</span>
      </button>
      <button class="data-btn" onclick={() => fileInput?.click()}>
        <span>📤 インポート</span>
        <span class="sub">JSON から復元 (上書き)</span>
      </button>
      <input
        type="file"
        accept="application/json,.json"
        bind:this={fileInput}
        onchange={handleImport}
        style="display: none;"
      />
    </div>
  </section>

  <div class="actions">
    <button class="close-btn" onclick={onclose}>閉じる</button>
  </div>
</BottomSheet>

<style>
  .group {
    margin-bottom: var(--space-lg);
  }
  .group-title {
    margin: 0 0 var(--space-sm);
    padding-bottom: 6px;
    border-bottom: 1px solid var(--color-border);
    font-size: var(--font-size-sm);
    font-weight: 700;
    color: var(--color-accent);
    letter-spacing: 0.04em;
  }
  .field {
    margin-bottom: var(--space-md);
  }
  .field-label {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-fg-muted);
    margin-bottom: var(--space-sm);
  }
  input[type='range'] {
    width: 100%;
    accent-color: var(--color-accent);
  }
  .seg-group {
    display: flex;
    gap: 3px;
    background: var(--color-border);
    padding: 3px;
    border-radius: var(--radius-sm);
  }
  .seg-group button {
    flex: 1;
    padding: 9px 6px;
    font-size: var(--font-size-sm);
    font-weight: 500;
    border-radius: 6px;
    color: var(--color-fg-muted);
  }
  .seg-group button.active {
    background: var(--color-bg);
    color: var(--color-fg);
    box-shadow: var(--shadow-sm);
    font-weight: 700;
  }
  .toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px var(--space-sm);
    background: var(--color-bg);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-base);
    cursor: pointer;
    margin-bottom: var(--space-sm);
  }
  .toggle-row:last-child {
    margin-bottom: 0;
  }
  .toggle-row input[type='checkbox'] {
    width: 22px;
    height: 22px;
    accent-color: var(--color-accent);
  }
  .data-actions {
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
  }
  .data-btn {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 12px var(--space-md);
    background: var(--color-bg);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    text-align: left;
    color: var(--color-fg);
    font-size: var(--font-size-base);
    font-weight: 600;
  }
  .data-btn:active {
    background: var(--color-border);
  }
  .data-btn .sub {
    font-size: 11px;
    color: var(--color-fg-muted);
    font-weight: 400;
  }
  .actions {
    margin-top: var(--space-lg);
  }
  .close-btn {
    width: 100%;
    padding: 12px;
    border-radius: var(--radius-sm);
    font-weight: 600;
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }
</style>
